import { Input, Typography } from "antd";
import { STEP_INPUT_POLICIES } from "components/CC/config/stepInputPolicies";
import { useSelectedNode } from "components/CC/hooks/useDiagramActions";
import { breakerFormSchema, type BreakerFormData } from "components/CC/Nodes/BreakerNode/Forms/formSchema";
import { FormValueSync } from "components/CC/UI/FormValueSync";
import { CustomFieldData } from "components/CC/UI/ParameterPicker/parameterUtils";
import { useBuildParameterPickerCategories } from "components/CC/UI/ParameterPicker/useBuildParameterPickerCategories";
import { TagTextInput } from "components/CC/UI/TagInput/TagTextInput";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider.tsx";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { useEffect, useMemo } from "react";
import { FormItem } from "react-hook-form-antd";

interface FormProps {
    onChange?: (data: BreakerFormData) => void;
    onRegisterValidator?: (fn: () => Promise<boolean>) => void;
    formId: string;
    formFields?: App.Data.FormFieldData[];
    customFields?: CustomFieldData[];
}

export const BreakerForm = ({ onChange, onRegisterValidator, formId, formFields, customFields }: FormProps) => {
    const { selectedNode } = useSelectedNode();
    return (
        <GeneralFormProvider<BreakerFormData>
            key={selectedNode?.id || "create"}
            id={formId}
            schema={breakerFormSchema}
            defaultValues={selectedNode?.formData as BreakerFormData}
        >
            <FormContent
                onChange={onChange}
                onRegisterValidator={onRegisterValidator}
                formFields={formFields}
                customFields={customFields}
            />
        </GeneralFormProvider>
    );
};

interface FormContentProps {
    onChange?: (data: BreakerFormData) => void;
    onRegisterValidator?: (fn: () => Promise<boolean>) => void;
    formFields?: App.Data.FormFieldData[];
    customFields?: CustomFieldData[];
}

const FormContent = ({ onChange, onRegisterValidator, formFields, customFields }: FormContentProps) => {
    const { control, trigger } = useGeneralForm<BreakerFormData>();

    useEffect(() => {
        onRegisterValidator?.(() => trigger());
    }, [onRegisterValidator, trigger]);
    const { selectedNode } = useSelectedNode();
    const { t } = usePaldockTranslation("components.connection-creator");

    const buildCategories = useBuildParameterPickerCategories({
        t,
        formFields,
        customFields,
        currentNodeId: selectedNode?.id,
    });

    const categories = useMemo(
        () =>
            buildCategories({ allowedCategories: [...STEP_INPUT_POLICIES.breaker.maxRepetition.ui.allowedCategories] }),
        [buildCategories]
    );

    return (
        <div className="bg-white rounded-lg p-4 m-3">
            <FormValueSync<BreakerFormData> onChange={onChange} />
            {/* Header with title and active toggle */}
            <div className="flex justify-between items-center mb-4">
                <Typography.Title level={4} className="!mb-0">
                    {t("nodes.breaker.forms.settings")}
                </Typography.Title>
                {/* Active toggle — hidden until BE is_enabled is wired up
                <Controller
                    control={control}
                    name="active"
                    render={({ field }) => (
                        <div className="flex items-center gap-2">
                            <Switch checked={field.value} onChange={field.onChange} />
                            <Typography.Text type="secondary">{t("nodes.breaker.forms.active")}</Typography.Text>
                        </div>
                    )}
                /> */}
            </div>

            {/* Title and Subtitle row */}
            <div className="flex gap-4 mb-4">
                <FormItem
                    className="flex-1 !mb-0"
                    control={control}
                    name="title"
                    label={t("nodes.breaker.forms.title")}
                >
                    <Input placeholder={t("nodes.breaker.forms.title_placeholder")} />
                </FormItem>
                <FormItem
                    className="flex-1 !mb-0"
                    control={control}
                    name="subtitle"
                    label={t("nodes.breaker.forms.subtitle")}
                >
                    <Input placeholder={t("nodes.breaker.forms.subtitle_placeholder")} />
                </FormItem>
            </div>

            {/* Maximum repetition */}
            <FormItem
                className="!mb-0"
                control={control}
                name="maxRepetition"
                label={t("nodes.breaker.forms.max_repetition")}
            >
                <TagTextInput
                    placeholder={t("nodes.breaker.forms.max_repetition_placeholder")}
                    categories={categories}
                    mode={STEP_INPUT_POLICIES.breaker.maxRepetition.ui.mode}
                />
            </FormItem>
        </div>
    );
};
